import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithEmailLink,
  signOut,
} from 'firebase/auth'
import api from '@/plugins/axios'
import { auth } from '@/plugins/firebase'
import { clearMockSession, mockToken, readMockSession, writeMockSession } from '@/plugins/mockSession'
import { GOOGLE_CLIENT_ID, MOCK_AUTH, ROLES } from '@/config'

/** Utente finto usato quando VITE_MOCK_AUTH=true: l'API locale lo accetta con un token mock. */
function buildMockUser(role = ROLES.ADMIN) {
  return {
    id: `dev-${role}`,
    name: role === ROLES.ADMIN ? 'Admin locale' : 'Utente locale',
    email: `dev-${role}@mock.local`,
    roles: role === ROLES.ADMIN ? [ROLES.ADMIN, ROLES.USER] : [ROLES.USER],
  }
}

/** Email a cui e' stato mandato il link: serve per completare l'accesso nello stesso browser. */
const EMAIL_LINK_KEY = 'kris-agent-email-link'

function readStoredEmail() {
  try {
    return localStorage.getItem(EMAIL_LINK_KEY) || ''
  } catch {
    return ''
  }
}

function storeEmail(email) {
  try {
    if (email) localStorage.setItem(EMAIL_LINK_KEY, email)
    else localStorage.removeItem(EMAIL_LINK_KEY)
  } catch {
    // Senza localStorage l'email verra' chiesta di nuovo all'apertura del link
  }
}

/**
 * Profilo applicativo dopo un accesso Firebase. Il 401 qui significa "utente
 * non abilitato", non "sessione scaduta": si esce e si mostra il motivo.
 */
async function profileAfterSignIn(firebaseUser, onSuccess, onNotEnabled) {
  const token = await firebaseUser.getIdToken()
  try {
    const { data: profile } = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
      skipAuthRedirect: true,
    })
    onSuccess?.(profile)
  } catch (error) {
    if (error.response?.status === 401) {
      await signOut(auth)
      onNotEnabled?.(
        error.response?.data?.message === 'User blocked'
          ? 'Il tuo account e’ bloccato.'
          : 'Non e’ stato possibile registrare il tuo account: serve un’email verificata.'
      )
      return
    }
    throw error
  }
}

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve()

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = resolve
    script.onerror = () => reject(new Error('Impossibile caricare Google Identity Services'))
    document.head.appendChild(script)
  })
}

const AuthService = {
  /**
   * Risolve con l'utente autenticato (o null) appena Firebase ha ripristinato
   * la sessione. Va sempre atteso prima di decidere un redirect.
   */
  waitForAuth() {
    if (MOCK_AUTH) return Promise.resolve(readMockSession())

    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe()
        resolve(user)
      })
    })
  },

  /** Profilo applicativo (ruoli compresi) letto dal backend, anche con il login finto. */
  async fetchProfile() {
    const { data } = await api.get('/auth/me')
    return data
  },

  async getToken() {
    if (MOCK_AUTH) return mockToken()
    return (await auth?.currentUser?.getIdToken()) ?? null
  },

  /**
   * Inizializza il bottone Google e gestisce lo scambio credential -> Firebase -> profilo.
   * @param {(user: object) => void} onSuccess       chiamato con il profilo applicativo
   * @param {(message: string) => void} onNotEnabled chiamato se il backend risponde 401
   */
  async initGoogleLogin(onSuccess, onNotEnabled) {
    if (!GOOGLE_CLIENT_ID) {
      throw new Error('[auth] VITE_GOOGLE_CLIENT_ID mancante. Valorizza il .env oppure imposta VITE_MOCK_AUTH=true.')
    }

    await loadGoogleScript()

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        const credential = GoogleAuthProvider.credential(response.credential)
        const result = await signInWithCredential(auth, credential)
        await profileAfterSignIn(result.user, onSuccess, onNotEnabled)
      },
    })
  },

  /**
   * Manda un link di accesso all'email. Il link riporta a questa pagina di login
   * con lo stesso `redirect`, cosi' un accesso partito da un client MCP prosegue
   * fino all'autorizzazione.
   */
  async sendEmailLink(email, redirect) {
    const url = new URL('/auth/login', window.location.origin)
    if (redirect) url.searchParams.set('redirect', redirect)
    await sendSignInLinkToEmail(auth, email, { url: url.toString(), handleCodeInApp: true })
    storeEmail(email)
  },

  /** Vero se la pagina e' stata aperta dal link ricevuto per email. */
  isEmailLink() {
    return !MOCK_AUTH && isSignInWithEmailLink(auth, window.location.href)
  },

  /** Email salvata quando il link e' stato chiesto da questo browser. */
  storedEmail() {
    return readStoredEmail()
  },

  async completeEmailLink(email, onSuccess, onNotEnabled) {
    const result = await signInWithEmailLink(auth, email, window.location.href)
    storeEmail('')
    await profileAfterSignIn(result.user, onSuccess, onNotEnabled)
  },

  renderGoogleButton(elementId) {
    window.google.accounts.id.renderButton(document.getElementById(elementId), {
      theme: 'outline',
      size: 'large',
      width: '100%',
    })
  },

  /** Login finto, disponibile solo con VITE_MOCK_AUTH=true. */
  mockLogin(role = ROLES.ADMIN) {
    const user = buildMockUser(role)
    writeMockSession(user)
    return user
  },

  async logout() {
    if (MOCK_AUTH) {
      clearMockSession()
      return
    }
    await signOut(auth)
  },
}

export default AuthService
